import Image from '@base/FutureImage';
import styled from '@emotion/styled';
import { Spacings, Fonts } from '@styles/index';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { getIsLoggedIn } from '@api/selectors/auth';
import pushRoute from '@utils/pushRoute';
import useOfflineCentreStore from '@stores/city-specific-offline-centres';
import Typography from 'aquilla/core/atoms/Typography';
import AquillaButton from 'aquilla/core/molecules/Button';
import { useEffect, useState } from 'react';
import cookies from '@utils/cookies';
import BookAVisit from '../CitySpecificOfflineCentres/BookAVisitDrawer';

const RootContainer = styled.div`
  position: relative;
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  border-radius: ${Spacings.SPACING_2B};
  border: ${Spacings.SPACING_1} solid;
  border-color: var(--color-i-divider);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  :hover {
    background-color: var(--color-i-gray-98);
    border-color: var(--color-i-black);
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  height: ${Spacings.SPACING_50B};
  border-radius: ${Spacings.SPACING_2B};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const StyImage = styled(Image)`
  border-radius: ${Spacings.SPACING_2B} ${Spacings.SPACING_2B} 0px 0px;
`;

const BreadcrumbContainer = styled.div`
  width: fit-content;
  height: 24px;
  display: flex;
  position: absolute;
  bottom: 0;
  left: 0;
  margin: 0px 0px ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
  border-radius: ${Spacings.SPACING_4B};
`;

const BreadcrumbBgWrapper = styled.div`
  display: flex;
  position: absolute;
  width: 100%;
  height: 100%;
`;

const BreadcrumbTextContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${Spacings.SPACING_2};
  align-items: center;
  z-index: 1;
  padding: ${Spacings.SPACING_1B} ${Spacings.SPACING_13};
`;

const BreadcrumbTextWrapper = styled.div`
  color: var(--color-i-brown-2);
  font-size: ${Fonts.H7};
  font-weight: ${Fonts.HEAVY};
  line-height: ${Spacings.SPACING_4B};
`;

const ContentContainer = styled.div`
  width: 100%;
  padding: ${Spacings.SPACING_4B};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${Spacings.SPACING_1B};
  flex: 1;
`;

const DirectionContainer = styled.div`
  width: fit-content;
  padding: ${Spacings.SPACING_6} ${Spacings.SPACING_3B};
  border-radius: ${Spacings.SPACING_6B};
  gap: ${Spacings.SPACING_2};
  margin-top: ${Spacings.SPACING_3B};
  background-color: var(--color-i-divider);
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const DirectionImageWrapper = styled.span`
  width: ${Spacings.SPACING_4B};
  height: ${Spacings.SPACING_4B};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DirectionTextWrapper = styled.span`
  color: var(--color-i-black);
  font-size: ${Fonts.H7};
  font-weight: ${Fonts.SEMIBOLD};
  text-decoration: underline;
`;

const DistanceTextWrapper = styled.div`
  color: var(--color-i-black);
  font-size: ${Fonts.H7};
  font-weight: ${Fonts.SEMIBOLD};
`;

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: ${Spacings.SPACING_4B};
  gap: ${Spacings.SPACING_4B};
`;

const StyAquillaButton = styled(AquillaButton)`
  width: 100%;
  height: ${Spacings.SPACING_12B};
  border-radius: ${Spacings.SPACING_2B};
  font-size: ${Fonts.H6};
  font-weight: ${Fonts.HEAVY};
`;

const CentreCard = ({ centreData }) => {
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const { setBookAVisitFlow } = useOfflineCentreStore();

  const [showBookVisit, setShowBookVisit] = useState(false);
  const [goalUID, setGoalUID] = useState(null);
  const [centreUID, setCentreUID] = useState(null);
  const [viewDetailsFlow, setViewDetailsFlow] = useState(false);
  const [clickedViewDetails, setClickedViewDetails] = useState(false);

  useEffect(() => {
    setGoalUID(cookies.readCookie('selectedGoalUid'));
  }, []);

  useEffect(() => {
    if (isLoggedIn && showBookVisit) setBookAVisitFlow(true);
  }, [isLoggedIn, setBookAVisitFlow, showBookVisit]);

  const goalString = centreData?.availableGoals?.join(', ');
  const distance = centreData?.distanceInKm;
  const address = centreData?.address || '\xa0';

  const onGetDirectionClicked = (url) => {
    window.open(url);
  };

  const handleVisitCentre = () => {
    setShowBookVisit(true);
    setCentreUID(centreData?.centreUid);
    if (!isLoggedIn) {
      openLoginDrawer();
    } else {
      setBookAVisitFlow(true);
    }
  };

  const handleCentreRouting = () => {
    setClickedViewDetails(true);
    if (!isLoggedIn) {
      openLoginDrawer();
    } else {
      setViewDetailsFlow(true);
    }
  };

  useEffect(() => {
    if (isLoggedIn && clickedViewDetails) setViewDetailsFlow(true);
  }, [clickedViewDetails, isLoggedIn]);

  useEffect(() => {
    if (viewDetailsFlow && isLoggedIn) {
      if (centreData?.availableUids?.length >= 1) {
        const currGoalUID = centreData?.availableUids[0];
        pushRoute({
          pathname: '/goal/-/[goalUID]/centre/-/[centreUID]',
          query: {
            goalUID: currGoalUID,
            centreUID: centreData?.centreUid
          }
        });
      }
      setClickedViewDetails(false);
      setViewDetailsFlow(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewDetailsFlow]);

  return (
    <RootContainer>
      <Container>
        <ImageContainer>
          <StyImage
            src={centreData?.imageUrl}
            layout="fill"
            objectFit="cover"
            alt="Centre Image"
          />
          {centreData?.breadcrumbText !== '' && (
            <BreadcrumbContainer>
              <BreadcrumbBgWrapper>
                <Image
                  src="allCentre/bcTextBg.svg"
                  layout="fill"
                  objectFit="fill"
                  alt="Location Icon"
                />
              </BreadcrumbBgWrapper>
              <BreadcrumbTextContainer>
                <Image
                  src="allCentre/BreadCrumbIcon.svg"
                  width={16}
                  height={16}
                  alt="Breadcrumb Text Icon"
                />
                <BreadcrumbTextWrapper>
                  {centreData?.breadcrumbText}
                </BreadcrumbTextWrapper>
              </BreadcrumbTextContainer>
            </BreadcrumbContainer>
          )}
        </ImageContainer>
        <ContentContainer>
          <Typography variant="h4">{centreData?.city}</Typography>
          <Typography variant="p2" color="var(--color-i-gray-13)">
            {goalString}
          </Typography>
          <Typography variant="p2" color="var(--color-i-gray-13)">
            {address}
          </Typography>
          <DirectionContainer>
            <DirectionImageWrapper>
              <Image
                src="allCentre/HollowArrowBlack.svg"
                width={10}
                height={10}
                alt="Location Icon"
              />
            </DirectionImageWrapper>
            {distance && (
              <DistanceTextWrapper>{`Approx. ${Math.floor(
                distance
              )} km away •\u00A0`}</DistanceTextWrapper>
            )}
            <DirectionTextWrapper
              onClick={() => onGetDirectionClicked(centreData?.location)}
            >
              Get directions
            </DirectionTextWrapper>
          </DirectionContainer>
          <ButtonContainer>
            <StyAquillaButton
              onClick={() => {
                handleVisitCentre();
              }}
              variant="primary"
            >
              Book A Visit
            </StyAquillaButton>
            <StyAquillaButton
              onClick={() => {
                handleCentreRouting();
              }}
              variant="secondary"
            >
              View Details
            </StyAquillaButton>
          </ButtonContainer>
        </ContentContainer>
      </Container>
      {showBookVisit && (
        <BookAVisit newGoalPage currentCentreDetails={{ goalUID, centreUID }} />
      )}
    </RootContainer>
  );
};

export default CentreCard;
